import { hashString, safe } from '@w/utils'
import { getTextFromChars, makeChars, type Block, type Blocks } from './block'

export type TranslationUnit = {
  blockId: string
  html: string
  hash: string
  /** Links and marks keep source attributes; their children remain translatable. */
  placeholders: Block[]
}
export type TranslationRow = { blockId: string; sourceHash: string; text: string | null }

const formats: Record<string, number> = { b: 1, i: 2, s: 4, u: 8, code: 16 }
const containers = new Set(['p', 'h', 'li', 'td', 'quote'])
const inline = new Set(['text', 'chars', 'em', 'a', 'l', 'cloze', 'code', 'mention', 'question', 'br', 'comment', 'criticComment', 'page', 'pageLink', 'diff', 'review'])

export function isInline(block: Block) {
  return inline.has(block.type) && (block.type !== 'question' || block.inline === true)
}
function escapeHtml(text: string) {
  return text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;')
}
function decodeHtml(text: string) {
  return text.replaceAll(/&(#x[\da-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (entity, name: string) => {
    const named: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00A0' }
    if (!name.startsWith('#')) return named[name.toLowerCase()] ?? entity
    const value = name[1].toLowerCase() === 'x' ? Number.parseInt(name.slice(2), 16) : Number(name.slice(1))
    return value > 0 && value <= 0x10ffff ? String.fromCodePoint(value) : '\uFFFD'
  })
}
function visible(block: Block): string {
  if (block.type === 'text') return block.text
  if (block.type === 'chars') return getTextFromChars(block)
  if ('children' in block) return (block.children ?? []).map(child => visible(child)).join('')
  if (block.type === 'question') return (block.question ?? []).map(child => visible(child)).join('')
  return ''
}

function unit(id: string, children: Blocks): TranslationUnit | undefined {
  const placeholders: Block[] = []
  let translatable = ''
  function html(block: Block, hashing: boolean): string {
    if (block.type === 'text' || block.type === 'chars') {
      const text = visible(block)
      if (!hashing) translatable += text
      let result = escapeHtml(text)
      for (const [tag, bit] of Object.entries(formats)) {
        if (block.type === 'chars' && ((block.format ?? 0) & bit)) result = `<${tag}>${result}</${tag}>`
      }
      return result
    }
    const index = hashing ? placeholders.indexOf(block) : placeholders.push(block) - 1
    if (block.type === 'em') return `<mark data-i="${index}">${block.children.map(child => html(child, hashing)).join('')}</mark>`
    if (block.type === 'a') return `<a data-i="${index}">${block.children.map(child => html(child, hashing)).join('')}</a>`
    return `<span translate="no" data-i="${index}">${hashing ? '' : escapeHtml(visible(block))}</span>`
  }
  const markup = children.map(child => html(child, false)).join('')
  if (!translatable.trim()) return undefined
  return { blockId: id, html: markup, hash: hashString(children.map(child => html(child, true)).join('')), placeholders }
}

/** Host filter: true keeps a choice question's options as authored (no option units). `previous` is the question's
 * preceding sibling. */
export type KeepOptions = (question: Extract<Block, { type: 'question' }>, previous: Block | undefined) => boolean

/** Shared traversal gives extraction and overlay identical keys. Direct question
 * prompts use the question id; options use :option:N (none when `keepOptions` holds),
 * explanations :explanation, and cloze tips :tip. */
function visitUnits(blocks: Blocks, transform: (id: string, children: Blocks) => Blocks, keepOptions?: KeepOptions): Blocks {
  function field(id: string, children: Blocks): Blocks {
    return children.every(child => isInline(child)) ? transform(id, children.map(child => walk(child))) : walkAll(children)
  }
  function walkAll(children: Blocks): Blocks {
    return children.map((child, index) => walk(child, children[index - 1]))
  }
  function walk(block: Block, previous?: Block): Block {
    if (block.type === 'question') {
      if (block.inline) return block
      return { ...block,
        ...(block.question && { question: field(block.id, block.question) }),
        ...(block.explanation && { explanation: field(`${block.id}:explanation`, block.explanation) }),
        ...(('opts' in block) && !keepOptions?.(block, previous) && { opts: block.opts.map((option, index) => field(`${block.id}:option:${index}`, option)) }),
      }
    }
    if (block.type === 'cloze') {
      if (!block.tip) return block
      const translated = transform(`${block.id}:tip`, [{ id: `${block.id}:tip:text`, type: 'text', text: block.tip }])
      return { ...block, tip: translated.map(child => visible(child)).join('') }
    }
    if (containers.has(block.type) && 'children' in block && block.children) {
      return { ...block, children: field(block.id, block.children) } as Block
    }
    // A language span is opaque, but a cloze inside it still carries a tip unit.
    if (block.type === 'l') return { ...block, children: block.children.map(child => walk(child)) }
    // Opaque inline content, preformatted code and graded answers are never units.
    if (isInline(block) || block.type === 'pre') return block
    if ('children' in block && block.children) return { ...block, children: walkAll(block.children) } as Block
    return block
  }
  return walkAll(blocks)
}

export function translationUnits(blocks: Blocks, options: { keepOptions?: KeepOptions } = {}): TranslationUnit[] {
  const result: TranslationUnit[] = []
  visitUnits(blocks, (id, children) => {
    const next = unit(id, children)
    if (next) result.push(next)
    return children
  }, options.keepOptions)
  return result
}

function mapAll(blocks: Blocks, fn: (block: Block) => Block): Blocks {
  return blocks.map(block => {
    let next = block
    if ('children' in next && next.children) next = { ...next, children: mapAll(next.children, fn) } as Block
    if (next.type === 'question') next = { ...next,
      ...(next.question && { question: mapAll(next.question, fn) }),
      ...(next.explanation && { explanation: mapAll(next.explanation, fn) }),
      ...(('opts' in next) && { opts: next.opts.map(option => mapAll(option, fn)) }),
    } as Block
    return fn(next)
  })
}

/** A span tagged with the BCP 47 `fonipa` variant (e.g. `und-fonipa`, `de-fonipa`) is a phonetic transcription, not
 * studied text: it has no meaning to gloss. */
function glossed(block: Block): boolean {
  return block.type === 'l' && !block.lang.toLowerCase().split('-').includes('fonipa')
}

export function glossUnits(blocks: Blocks): { blockId: string; text: string; hash: string }[] {
  const result: { blockId: string; text: string; hash: string }[] = []
  mapAll(blocks, block => {
    if (glossed(block)) {
      const text = visible(block)
      result.push({ blockId: block.id, text, hash: hashString(text) })
    }
    return block
  })
  return result
}

type Element = { tag: string; index?: number; children: HtmlNode[] }
type HtmlNode = string | Element

/** Small runtime-independent scanner. Accepts quote styles, tag case and extra
 * attributes; rejects unbalanced/unknown tags rather than guessing at answers. */
function scan(html: string): HtmlNode[] | undefined {
  const root: Element = { tag: '', children: [] }
  const stack = [root]
  const tokens = /[^<]+|<\/?[a-z][^>"']*(?:(?:"[^"]*"|'[^']*')[^>"']*)*>/giy
  let offset = 0
  while (offset < html.length) {
    tokens.lastIndex = offset
    const match = tokens.exec(html)
    if (!match) return undefined
    const token = match[0]
    offset = tokens.lastIndex
    const parent = safe(stack.at(-1))
    if (!token.startsWith('<')) { parent.children.push(decodeHtml(token)); continue }
    const tagMatch = /^<(\/)?([a-z]+)([\s\S]*?)>$/i.exec(token)
    if (!tagMatch) return undefined
    const tag = tagMatch[2].toLowerCase()
    if (!Object.hasOwn(formats, tag) && !['mark', 'a', 'span'].includes(tag)) return undefined
    if (tagMatch[1]) {
      if (tagMatch[3].trim() || stack.length === 1 || parent.tag !== tag) return undefined
      stack.pop()
      continue
    }
    let attributes = tagMatch[3].trim()
    const selfClosing = attributes.endsWith('/')
    if (selfClosing) attributes = attributes.slice(0, -1).trim()
    const attrs = new Map<string, string>()
    while (attributes) {
      const attr = /^([\w:-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?(?:\s+|$)/.exec(attributes)
      if (!attr || attrs.has(attr[1].toLowerCase())) return undefined
      attrs.set(attr[1].toLowerCase(), attr[2] ?? attr[3] ?? attr[4] ?? '')
      attributes = attributes.slice(attr[0].length)
    }
    const index = attrs.get('data-i')
    if (index !== undefined && !/^(0|[1-9]\d*)$/.test(index)) return undefined
    const element: Element = { tag, ...(index !== undefined && { index: Number(index) }), children: [] }
    parent.children.push(element)
    if (!selfClosing) stack.push(element)
  }
  return stack.length === 1 ? root.children : undefined
}

function validated(unit: TranslationUnit, html: string): HtmlNode[] | undefined {
  const nodes = scan(html)
  if (!nodes) return undefined
  const seen = new Set<number>()
  function valid(node: HtmlNode): boolean {
    if (typeof node === 'string') return true
    if (node.tag === 'span' || node.tag === 'a' || node.tag === 'mark') {
      if (node.index === undefined || seen.has(node.index)) return false
      const source = unit.placeholders[node.index]
      if (!source || (source.type === 'a' ? 'a' : source.type === 'em' ? 'mark' : 'span') !== node.tag) return false
      seen.add(node.index)
    } else if (node.index !== undefined) return false
    // Restoring an opaque span discards its returned body. An indexed descendant
    // would disappear with it, even if the overall index counts looked correct.
    if (node.tag === 'span' && node.children.some(child => hasIndex(child))) return false
    return node.children.every(child => valid(child))
  }
  return nodes.every(child => valid(child)) && seen.size === unit.placeholders.length ? nodes : undefined
}

export function validateTranslatedUnit(unit: TranslationUnit, html: string): boolean {
  return validated(unit, html) !== undefined
}

function hasIndex(node: HtmlNode): boolean {
  return typeof node !== 'string' && (node.index !== undefined || node.children.some(child => hasIndex(child)))
}

function rebuild(nodes: HtmlNode[], source: TranslationUnit, format = 0, path = source.blockId): Blocks {
  return nodes.flatMap((node, index): Blocks => {
    const id = `${path}:translated:${index}`
    if (typeof node === 'string') return node ? [format ? makeChars({ id, text: node, format }) : { id, type: 'text', text: node }] : []
    if (node.tag === 'span') return [source.placeholders[safe(node.index)]]
    if (node.tag === 'a' || node.tag === 'mark') {
      const link = source.placeholders[safe(node.index)]
      return [{ ...link, children: rebuild(node.children, source, format, id) } as Block]
    }
    return rebuild(node.children, source, format | formats[node.tag], id)
  })
}

export function applyTranslation(blocks: Blocks, rows: TranslationRow[]): Blocks {
  const byId = new Map(rows.map(row => [row.blockId, row]))
  const translated = visitUnits(blocks, (id, children) => {
    const row = byId.get(id)
    const source = unit(id, children)
    if (!source || (row?.text === null || row?.text === undefined)) return children
    const nodes = validated(source, row.text)
    return nodes ? rebuild(nodes, source) : children
  })
  return mapAll(translated, block => {
    const row = byId.get(block.id)
    return glossed(block) && row?.text !== null && row?.text !== undefined ? { ...block, $gloss: row.text } : block
  })
}
